<template>
  <div id="payment-cadence-modal">
    <Modal modalId="change-payment-cadence-modal" size="modal-lg modal-size" @hidden="closeChangeCadenceModal()">
      <div v-if="this.loadingData">
        <b-row class="text-center pt-5 pb-5">
          <b-col>
            <b-spinner style="width: 10rem; height: 10rem;" variant="warning" label="Spinning"></b-spinner>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <ModalHeader
          :title="$t('xManagePlan')"
          icon="far fa-circle-dollar colorOrange mr-1"
          :style="{ padding: '4px 12px' }"
        >
          <div class="cols-auto">
            <button
              type="button"
              class="btn btn-icon-button btn-icon-button--gray"
              @click="closeChangeCadenceModal()"
              data-test-hook="CloseModalBtn"
            >
              <i class="fas fa-times" />
            </button>
          </div>
        </ModalHeader>
        <div v-if="paying">
          <b-row class="text-center">
            <b-col>
              <b-spinner style="width: 10rem; height: 10rem;" variant="warning" label="Spinning"></b-spinner>
            </b-col>
          </b-row>
        </div>
        <div v-else>
          <ModalBody>
            <div class="row mb-3">
              <div class="col">
                <div data-test-hook="YouSaveMoneyText">
                  <div v-if="this.yearly">
                    {{ $t('xSaveMoneyYearlyText', { 0: this.currentSubPrice }) }}
                  </div>
                  <div v-else-if="this.monthly">
                    {{ $t('xSaveMoneyMonthlyText', { 0: this.getNewPlanPrice() }) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <span class="text" data-test-hook="RenewalDetails">
                  {{ $t('xRenewalDetails') }}
                  <ul>
                    <li>
                      {{
                        $t('xCurrentPlanDetailsText', {
                          0: $moment(this.purchasedDate).format('MM/DD/YYYY'),
                          1: $moment(endDate).format('MM/DD/YYYY')
                        })
                      }}
                    </li>
                    <!-- new plan starts on the same day the old plan ends -->
                    <li v-if="this.yearly">
                      {{
                        $t('xNewMonthlyPaymentText', {
                          0: this.getNewPlanPrice(),
                          1: $moment(endDate).format('MM/DD/YYYY')
                        })
                      }}
                    </li>
                    <li v-else-if="this.monthly">
                      {{
                        $t('xNewYearlyPaymentText', {
                          0: this.getNewPlanPrice(),
                          1: $moment(endDate).format('MM/DD/YYYY')
                        })
                      }}
                    </li>
                  </ul>
                </span>
              </div>
            </div>
            <div class="row" data-test-hook="AreYouSureYouWantToSwitchText">
              <div v-if="this.yearly" class="col">
                {{ $t('xAreYouSureMonthlyText') }}
              </div>
              <div v-else-if="this.monthly" class="col">
                {{ $t('xAreYouSureYearlyText') }}
              </div>
            </div>
          </ModalBody>
          <ModalFooter class="d-flex justify-content-center pr-0 pl-0">
            <div v-if="this.yearly" class="modal-buttons">
              <button
                type="button"
                class="btn btn-outline-warning"
                @click="closeChangeCadenceModal()"
                :title="$t('xKeepYearlyBtn')"
                data-test-hook="KeepCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xKeepYearlyBtn') }}
              </button>
              <button
                type="button"
                class="btn btn-warning ml-2"
                @click="changePaymentCadence()"
                :title="$t('xSwitchMonthlyBtn')"
                data-test-hook="SwitchCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xSwitchMonthlyBtn') }}
              </button>
            </div>
            <div v-else-if="this.monthly" class="modal-buttons">
              <button
                type="button"
                class="btn btn-outline-warning"
                @click="closeChangeCadenceModal()"
                :title="$t('xKeepMonthlyBtn')"
                data-test-hook="KeepCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xKeepMonthlyBtn') }}
              </button>
              <button
                type="button"
                class="btn btn-warning ml-2"
                @click="changePaymentCadence()"
                :title="$t('xSwitchYearlyBtn')"
                data-test-hook="SwitchCadenceBtn"
                :disabled="this.paying"
              >
                {{ $t('xSwitchYearlyBtn') }}
              </button>
            </div>
          </ModalFooter>
        </div>
      </div>
    </Modal>
    <PaymentModal
      :modal-id="'change-cadence-payment'"
      :selected-product="selectedProduct"
      :account-profile="accountProfile"
      @submit="subscribeToNewProduct"
      @goBack="returnToManagePlanModal"
      @savePremiumFeaturesGettingReadyInfo="handleCadenceChange"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import * as Constants from '@/constants'
import PaymentModal from '@/components/Enrollment/Subscription/PaymentModal.vue'
import { isSubscriptionPendingChange } from '@/utils/subscriptions'
import {
  isDealerManaged,
  subscriptionDetailContainsApparatus,
  subscriptionDetailActive,
  pollForSubmissionUpdate,
  openModal,
  closeModal,
  pendingChangeIsPendingButAcknowledged,
  pendingChangeIsAlreadyActive
} from '@/utils/mixins'
import _ from 'lodash'

export default {
  components: { PaymentModal },
  name: 'PaymentCadenceModal',
  data() {
    return {
      apparatuses: null,
      accountData: null,
      purchasedDate: null,
      currentSubPrice: null,
      yearly: false,
      monthly: false,
      subscription: null,
      selectedProduct: null,
      productsArray: null,
      currentSubProductId: null,
      existingPaymentInfo: null,
      loadingData: false,
      errorMessages: null,
      expirationMonth: null,
      expirationYear: null,
      paying: false
    }
  },
  props: ['endDate', 'subscriptionStore'],
  inject: ['updateSubscriptionDetails'],
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile
    })
  },
  methods: {
    /** Validation function for use with global pollForSubmissionUpdate function for subscription change detection. Also returns a subscription if it finds one that matches the validation criteria.*/
    pollingValidator(newSubscriptionData) {
      const subscriptionApparatusIds = this.subscriptions[0]?.apparatuses.map(apparatus => apparatus.apparatusId) ?? []

      return newSubscriptionData.subscriptions?.find(
        sub =>
          // Sub must be active, pending change, or pending cancel.
          subscriptionDetailActive(sub.status) &&
          // A user cannot upgrade or update a DM plan. DM plans are managed in Fleet. A DM plan is for a singular apparatus (i.e., tied to only one HSB or FM).
          !isDealerManaged(sub?.product?.productId) &&
          sub.apparatuses?.length > 0 &&
          // Since they have to include all apparatuses in the plan change, we only need to check that one of those apparatuses is included.
          subscriptionDetailContainsApparatus(sub.apparatuses, subscriptionApparatusIds[0]) &&
          (pendingChangeIsAlreadyActive(sub, this.selectedProduct.productId) ||
            pendingChangeIsPendingButAcknowledged(sub, this.selectedProduct.productId))
      )
    },
    async handleCadenceChange() {
      // Poll for subscription details until the conditions of the validator are met (i.e., we find a subscription that includes the updates we expect).
      this.pollForSubmissionUpdate(
        'getSubscriptionDetails',
        this.accountProfile.organizationId,
        this.pollingValidator,
        'lg'
      )
        .then(newSubscriptionData => {
          const newSubscriptionDetails = this.pollingValidator(newSubscriptionData)
          // Show the green toast if the API has acknowledged our change (either immediately applied or pending).
          if (newSubscriptionDetails) {
            // Clear any existing pending changes because a new plan change will overwrite anything pending.
            this.$store.dispatch('deletePendingSubscriptionChanges')

            this.updateSubscriptionDetails(newSubscriptionData)
            this.$store.dispatch('displayToast', {
              title: this.$t('xSuccess'),
              message: this.$t('xPlanChangedSuccessfully'),
              variant: 'success'
            })
          }
          // Otherwise, processing is not complete yet, show the blue banners.
          else {
            const pendingChange = {
              apparatusIds: this.subscriptions[0].apparatuses.map(apparatus => apparatus.apparatusId) ?? [],
              pendingProductId: this.selectedProduct.productId,
              isPendingCancel: false
            }
            this.$store.dispatch('addPendingSubscriptionChanges', pendingChange)
          }
        })
        .catch(() => {
          this.showErrorToast()
        })
    },
    returnToManagePlanModal() {
      this.$bvModal.hide('change-cadence-payment')
      this.closeChangeCadenceModal()
      this.openModal('manage-plan-modal')
    },
    closeChangeCadenceModal() {
      this.closeModal('change-payment-cadence-modal')
    },
    async changePaymentCadence() {
      this.paying = true
      this.openModal('change-payment-cadence-modal')
      await this.$store.dispatch('getAccountProfile')
      this.closeChangeCadenceModal()
      this.$bvModal.show('change-cadence-payment')
      this.paying = false
    },
    getNewPlanPrice() {
      if (this.selectedProduct) {
        return this.selectedProduct?.cost
      }
    },
    subscribeToNewProduct({ resolve, reject }) {
      // gets the apparatus ids of the apparatuses that need to be added to the new plan
      const apparatusIds = this.subscriptions[0].apparatuses
        .filter(x => x.apparatusId)
        .map(apparatusIds => apparatusIds.apparatusId)
      this.$store
        .dispatch('createPurchaseIntent', {
          organizationId: this.accountData.organizationId,
          productId: this.selectedProduct?.productId,
          apparatusIds: apparatusIds
        })
        .then(() => {
          this.$store
            .dispatch('updateSubscriptionProduct', {
              organizationId: this.accountData?.organizationId,
              productId: this.selectedProduct?.productId
            })
            .then(response => {
              resolve(response)
              this.$bvModal.hide('change-cadence-payment')
            })
            .catch(() => {
              reject(this.$t('xPlanChangesFailed'))
            })
        })
        .catch(() => {
          reject(this.$t('xPlanChangesFailed'))
        })
    },
    /** Show red error toast. */
    showErrorToast() {
      this.$store.dispatch('displayToast', {
        title: this.$t('xSomethingWentWrong'),
        message: this.$t('xWaitAndTryAgainErrorText'),
        variant: 'danger'
      })
    },
    pollForSubmissionUpdate,
    openModal,
    closeModal
  },
  async mounted() {
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
      await this.$store
        .dispatch('getSubscriptionDetails', this.accountData.organizationId)
        .then(response => {
          // Find non canceled, non dealer managed subscriptions
          this.subscriptions = response.data?.subscriptions.filter(
            x => x.status !== Constants.SubscriptionDetailCanceled && x.store !== Constants.DealerSubscriptionStore
          )

          if (this.subscriptions && this.subscriptions?.length > 0) {
            const hasPendingChange = isSubscriptionPendingChange(this.subscriptions[0])
            this.purchasedDate = this.subscriptions[0]?.purchasedDate
            this.apparatuses = this.subscriptions[0]?.apparatuses
            let currentDuration = this.subscriptions[0]?.product?.durationInMonths
            this.currentSubPrice = this.subscriptions[0]?.product?.cost

            if (hasPendingChange) {
              currentDuration = this.subscriptions[0]?.pendingProduct?.durationInMonths
              this.currentSubPrice = this.subscriptions[0]?.pendingProduct?.cost
            }

            if (currentDuration === 12) {
              this.yearly = true
              // Replace yearly with monthly to reflect productId of desired plan
              this.currentSubProductId = this.subscriptions[0]?.product?.productId.replace('-yearly', '-monthly')
            } else {
              this.monthly = true
              // Replace monthly with yearly to reflect productId of desired plan
              this.currentSubProductId = this.subscriptions[0]?.product?.productId.replace('-monthly', '-yearly')
            }
          }
        })
        .catch(() => {
          this.showErrorToast()
        })

      // If there is a subscription that isn't canceled, check if it's NOT from Web, otherwise this is false
      const isCurrentSubFromDifferentStore =
        this.subscriptions &&
        this.subscriptions.length > 0 &&
        this.subscriptions[0].status !== Constants.SubscriptionDetailCanceled
          ? this.subscriptions[0].store === Constants.WebSubscriptionStore
          : false
      // FindStoreProducts throws an error if the store is incorrect
      if (isCurrentSubFromDifferentStore) {
        await this.$store
          .dispatch('getSubscriptionProductsByStoreOrganization', { organizationId: this.accountData.organizationId })
          .then(response => {
            this.productsArray = response.data?.products
            if (this.yearly) {
              const newProduct = this.productsArray.filter(
                x => x.durationInMonths === 1 && x.productId === this.currentSubProductId
              )
              this.selectedProduct = newProduct[0]
            } else if (this.monthly) {
              const newProduct = this.productsArray.filter(
                x => x.durationInMonths === 12 && x.productId === this.currentSubProductId
              )
              this.selectedProduct = newProduct[0]
            }
          })
          .catch(() => {
            this.showErrorToast()
          })
      }
    } else {
      this.$store.dispatch('getAccountProfile')
    }
  }
}
</script>
<!-- Had to overwrite the size of the modal to match what was shown in figma -->
<style lang="scss">
#payment-cadence-modal {
  .modal-size {
    max-width: 600px !important;
  }
}
</style>
